import { ChevronDown, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FacebookIcon, InstagramIcon } from "@/components/icons/platform-icons";
import { cn } from "@/lib/utils";
import type { SocialPlatform } from "../types";
import {
  type ChannelFilterValue,
  type FilterableChannel,
  channelLabel,
} from "../constants";

/**
 * Narrow the rule list to the accounts a brand actually cares about.
 *
 * Two independent axes, per the design: platform on the left, individual connected accounts on the
 * right. They intersect — picking Instagram and then two handles shows rules scoped to those two,
 * not to every Instagram account.
 *
 * Empty selections mean "everything". A brand that has ticked nothing is not asking to see nothing,
 * and a filter that empties the page by default would look like a broken list rather than a filter.
 */

interface ChannelFilterProps {
  channels: FilterableChannel[];
  value: ChannelFilterValue;
  onChange: (next: ChannelFilterValue) => void;
}

const PLATFORMS: { label: string; value: SocialPlatform }[] = [
  { label: "Facebook", value: "facebook" },
  { label: "Instagram", value: "instagram" },
];

const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item)
    ? list.filter((entry) => entry !== item)
    : [...list, item];

/** Number of axes actually narrowing the list, shown on the trigger so an active filter is visible when closed. */
const activeCount = (value: ChannelFilterValue): number =>
  (value.platforms.length > 0 ? 1 : 0) + (value.channelIds.length > 0 ? 1 : 0);

/**
 * `Horizontal/Full-width/ Subtle` — Figma 4235:75155 and 4235:75168.
 *
 * Sits under the first row of BOTH columns: "All Channels" and "Select All" are the select-all
 * controls for the list beneath them, not members of it, and the line is what says so.
 */
const SelectAllDivider = () => <div className="mx-2 my-2 h-px bg-gray-300" />;

const Row = ({
  label,
  checked,
  onCheckedChange,
  icon,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: () => void;
  icon?: React.ReactNode;
}) => (
  <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-ink-strong hover:bg-gray-100">
    <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
    {icon}
    <span className="truncate">{label}</span>
  </label>
);

export const ChannelFilter = ({
  channels,
  value,
  onChange,
}: ChannelFilterProps) => {
  const count = activeCount(value);

  // Only offer a platform the brand has actually connected. Ticking "Facebook" with no Facebook
  // account would empty the list and look like a bug.
  const availablePlatforms = PLATFORMS.filter((platform) =>
    channels.some((channel) => channel.platform === platform.value),
  );

  const visibleChannels =
    value.platforms.length > 0
      ? channels.filter((channel) => value.platforms.includes(channel.platform))
      : channels;

  /*
   * Each column reads its OWN axis. `allChannelsSelected` was derived from `channelIds` — the
   * accounts list — so "All Channels" ticked itself while Facebook and Instagram below it stayed
   * empty, and the panel claimed two contradictory things at once.
   *
   * Empty means everything, which is why the select-all row is checked when the list is empty
   * and why its children read as checked with it: nothing is excluded, so nothing should look
   * excluded.
   */
  const allChannelsSelected = value.platforms.length === 0;
  const allAccountsSelected = value.channelIds.length === 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-8 gap-1.5 rounded-full pl-3 pr-2.5 text-[13px] font-normal",
            count > 0 && "border-ink-strong text-ink-strong",
          )}
        >
          <ListFilter className="size-3.5" />
          Filter
          {count > 0 && (
            <span className="rounded-full bg-ink-strong px-1.5 text-[11px] leading-4 text-white">
              {count}
            </span>
          )}
          <ChevronDown className="size-3.5 text-gray-800" />
        </Button>
      </PopoverTrigger>

      {/* The same frosted surface as the dialogs and the dropdown menus — bg-popover is opaque,
			    so this panel was the one overlay in the feature that sat flat on the page. */}
      <PopoverContent
        align="end"
        className="w-[460px] rounded-2xl border-border/60 bg-white/80 p-0 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)] backdrop-blur-[6px]"
      >
        <div className="grid grid-cols-2">
          <div className="p-3">
            <p className="px-2 pb-1 text-xs text-gray-800">Channels</p>
            {/*
             * A transparent copy of the Accounts box. Only Accounts is bordered, but its
             * border and padding push its rows down 5px — so without matching the box
             * model here the two select-all dividers sat on different lines. The design
             * starts both lists at the same y.
             */}
            <div className="rounded-xl border border-transparent p-1">
              <Row
                label="All Channels"
                checked={allChannelsSelected}
                onCheckedChange={() => onChange({ ...value, platforms: [] })}
              />
              <SelectAllDivider />
              {availablePlatforms.map((platform) => (
                <Row
                  key={platform.value}
                  label={platform.label}
                  checked={
                    allChannelsSelected ||
                    value.platforms.includes(platform.value)
                  }
                  icon={
                    platform.value === "facebook" ? (
                      <FacebookIcon className="size-3.5 shrink-0" />
                    ) : (
                      <InstagramIcon className="size-3.5 shrink-0" />
                    )
                  }
                  onCheckedChange={() => {
                    const platforms = toggle(value.platforms, platform.value);
                    // Drop any account selection the new platform set no longer contains, so the two
                    // axes cannot disagree and silently produce an empty list.
                    const stillVisible = channels
                      .filter(
                        (channel) =>
                          platforms.length === 0 ||
                          platforms.includes(channel.platform),
                      )
                      .map((channel) => channel.channelId);
                    onChange({
                      platforms,
                      channelIds: value.channelIds.filter((id) =>
                        stillVisible.includes(id),
                      ),
                    });
                  }}
                />
              ))}
            </div>
          </div>

          {/*
           * Accounts sits in its OWN bordered box; Channels does not (design review, 20/08 —
           * Namrata's design). The asymmetry is the point: the left column is a short fixed
           * list of platforms, the right is an open-ended list of connected accounts that
           * scrolls, and the box is what says where that list ends. A dividing line alone
           * left the two reading as one long column.
           */}
          <div className="p-3">
            <p className="px-2 pb-1 text-xs text-gray-800">Accounts</p>
            {visibleChannels.length === 0 ? (
              <p className="px-2 py-2 text-[13px] text-gray-800">
                No accounts connected
              </p>
            ) : (
              <div className="max-h-[220px] overflow-y-auto rounded-xl border border-gray-300 p-1">
                <Row
                  label="Select All"
                  checked={allAccountsSelected}
                  onCheckedChange={() => onChange({ ...value, channelIds: [] })}
                />
                <SelectAllDivider />
                {visibleChannels.map((channel) => (
                  <Row
                    key={channel.channelId}
                    label={channelLabel(channel)}
                    checked={
                      allAccountsSelected ||
                      value.channelIds.includes(channel.channelId)
                    }
                    onCheckedChange={() =>
                      onChange({
                        ...value,
                        channelIds: toggle(value.channelIds, channel.channelId),
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
